import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import APIHandler from '../utils/APIHandler';
import { Container, Row, Col, Button, ListGroup, Card, Badge, Form, Spinner, Table, Navbar, Nav } from 'react-bootstrap';
import moment from 'moment';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import toastify CSS

function ClassPage() {

    const { classId: groupEmail } = useParams();
    const [students, setStudents] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false); // Page loading
    const [processingEnableDisableAll, setProcessingEnableDisableAll] = useState(false); // Main loader
    const [studentTimers, setStudentTimers] = useState({}); // Timer for each student
    const [countdowns, setCountdowns] = useState({}); // Countdown display for each student
    const [globalTimeoutDuration, setGlobalTimeoutDuration] = useState(45); // Default to 45 minutes
    const [sourceGroupEmail, setSourceGroupEmail] = useState(''); // For copying users
    const [groupNotFound, setGroupNotFound] = useState(false); // New state for handling group not found



    const fetchGroupMembersAndUsers = async () => {
        setLoading(true);
        try {
            const studentData = await APIHandler.fetchGroupMembers(groupEmail);
            const studentsFromFirestore = studentData.students || [];

            const groupUsersData = await APIHandler.getUsersInGroup(groupEmail);
            const groupUsers = groupUsersData.users || [];

            const updatedStudents = studentsFromFirestore.map(student => {
                const isInGroup = groupUsers.some(groupUser => groupUser === student.email);
                return { ...student, enabled: isInGroup, timeout_duration: student.timeout_duration, updatedAt: student.updatedAt };
            });

            setStudents(updatedStudents);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setGroupNotFound(true);  // Set group not found state
            } else {
                toast.error('Error fetching data');
            }
        } finally {
            setLoading(false);
        }
    };


    const checkStudentStatus = async (student) => {
        try {
            const groupUsersData = await APIHandler.getUsersInGroup(groupEmail);
            const groupUsers = groupUsersData.users || [];

            const isInGroup = groupUsers.some(groupUser => groupUser.email === student.email);

            // Only update if the status has changed
            setStudents(prevStudents =>
                prevStudents.map(stu =>
                    stu.email === student.email && stu.enabled !== isInGroup
                        ? { ...stu, enabled: isInGroup }
                        : stu
                )
            );
        } catch (error) {
            console.error('Error checking student status', error);
        }
    };
    useEffect(() => {


        fetchGroupMembersAndUsers();
    }, [groupEmail]);




    const disableAllStudents = async () => {
        const enabledStudents = students.filter(student => student.enabled);
        if (enabledStudents.length === 0) {
            toast.error('All students are already disabled.');
            return;
        }

        setProcessingEnableDisableAll(true);
        try {
            await Promise.all(enabledStudents.map(student => APIHandler.removeStudentFromGroup(groupEmail, student.email)));

            // Reset countdowns for each student
            const updatedStudents = students.map(student => {
                setCountdowns(prevCountdowns => ({
                    ...prevCountdowns,
                    [student.email]: '00:00' // Reset to zero when disabled
                }));
                return { ...student, enabled: false };
            });

            setStudents(updatedStudents);
            toast.success('All students have been disabled.');
        } catch (error) {
            toast.error('Failed to disable all students.');
        } finally {
            setProcessingEnableDisableAll(false);
        }
    };


    useEffect(() => {
        // Interval to update countdowns every second
        const intervalId = setInterval(() => {
            setCountdowns(prevCountdowns => {
                const newCountdowns = { ...prevCountdowns };

                students.forEach((student) => {
                    if (student.enabled && student.updatedAt && student.timeout_duration) {
                        const timeElapsed = moment().diff(moment(student.updatedAt), 'seconds');
                        const timeLeft = (student.timeout_duration * 60) - timeElapsed;

                        if (timeLeft > 0) {
                            const hours = Math.floor(timeLeft / 3600);
                            const minutes = Math.floor((timeLeft % 3600) / 60);
                            const seconds = timeLeft % 60;
                            newCountdowns[student.email] = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                        } else {
                            newCountdowns[student.email] = '00:00:00';

                            checkStudentStatus(student);

                        }
                    }
                });

                return newCountdowns;
            });
        }, 1000); // Update every second

        return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }, [students]); // Run effect only when students array changes


    const searchUsers = async () => {
        if (!inputValue) {
            toast.error('Please enter a name or email to search.');
            return;
        }

        setProcessingEnableDisableAll(true);
        try {
            const users = await APIHandler.searchUsersByName(inputValue);
            setSearchResults(users.slice(0, 10));
        } catch (error) {
            toast.error('Error searching for users');
        } finally {
            setProcessingEnableDisableAll(false);
        }
    };

    const addStudents = async () => {
        if (selectedUsers.length > 0) {
            setProcessingEnableDisableAll(true);
            try {
                const payload = {
                    class_id: groupEmail,
                    students: selectedUsers.map(user => ({
                        email: user.email,
                        name: user.name,
                    })),
                };

                const response = await APIHandler.addMembers(groupEmail, payload.students);

                if (response && response.students) {
                    setStudents([...students, ...response.students]);
                    setSelectedUsers([]);
                    toast.success('Students added successfully');
                } else {
                    toast.error('Error adding students');
                }
            } catch (error) {
                toast.error('Error adding students');
            } finally {
                setProcessingEnableDisableAll(false);
            }
        } else {
            toast.error('Please select at least one student to add');
        }
    };

    const enableAllStudents = async () => {
        const disabledStudents = students.filter(student => !student.enabled);
        if (disabledStudents.length === 0) {
            toast.error('All students are already enabled.');
            return;
        }

        setProcessingEnableDisableAll(true);
        try {
            const now = new Date().toISOString(); // Get current time
            await APIHandler.addEnabledStudentsToGroup(groupEmail, disabledStudents.map(student => ({
                email: student.email,
                timeout_duration: globalTimeoutDuration, // Use the global timer input
                updatedAt: now // Set the updatedAt timestamp to the current time
            })));

            // Immediately start the countdown for each student
            disabledStudents.forEach(student => {
                setCountdowns(prevCountdowns => ({
                    ...prevCountdowns,
                    [student.email]: `${globalTimeoutDuration}:00`
                }));
                student.updatedAt = now; // Update the state with the current time
            });

            const updatedStudents = students.map(student => ({ ...student, enabled: true, updatedAt: now }));
            setStudents(updatedStudents);
            toast.success('All students have been enabled.');
        } catch (error) {
            toast.error('Failed to enable all students.');
        } finally {
            setProcessingEnableDisableAll(false);
        }
    };

    const toggleStudentStatus = async (index) => {
        const updatedStudents = [...students];
        const student = updatedStudents[index];
        const timeoutDuration = studentTimers[student.email] || globalTimeoutDuration; // Use timer value or global

        setProcessingEnableDisableAll(true);
        try {
            const now = new Date().toISOString(); // Get the current time
            if (student.enabled) {
                await APIHandler.removeStudentFromGroup(groupEmail, student.email);
                student.enabled = false;
                toast.success(`Student ${student.name} disabled.`);
            } else {
                await APIHandler.addEnabledStudentsToGroup(groupEmail, [{
                    email: student.email,
                    timeout_duration: timeoutDuration,
                    updatedAt: now // Set timestamp to now
                }]);
                student.enabled = true;

                // Start the countdown
                setCountdowns(prevCountdowns => ({
                    ...prevCountdowns,
                    [student.email]: `${timeoutDuration}:00`
                }));
                student.updatedAt = now; // Update time
                toast.success(`Student ${student.name} enabled for ${timeoutDuration} minutes.`);
            }
        } catch (error) {
            toast.error('Failed to update student status');
        } finally {
            setProcessingEnableDisableAll(false);
            setStudents(updatedStudents);
        }
    };


    const deleteStudentFromFirestore = async (studentEmail) => {
        setProcessingEnableDisableAll(true);
        try {
            await APIHandler.deleteStudent(groupEmail, studentEmail);
            setStudents(students.filter(student => student.email !== studentEmail));
            toast.success(`Student ${studentEmail} removed successfully.`);
        } catch (error) {
            toast.error('Failed to remove student.');
        } finally {
            setProcessingEnableDisableAll(false);
        }
    };

    const copyUsersFromGroup = async () => {
        if (!sourceGroupEmail) {
            toast.error('Please enter a source group email.');
            return;
        }

        try {
            setProcessingEnableDisableAll(true);

            const response = await APIHandler.copyUsersFromGroup(sourceGroupEmail, groupEmail);


            if (response && response.added_users) {
                setStudents([...students, ...response.added_users]);
                toast.success('Users copied successfully');
            } else {
                toast.error('Error copying users');
            }
        } catch (error) {
            toast.error('Failed to copy users');
        } finally {
            setProcessingEnableDisableAll(false);
        }
    };

    return (
        <Container className="mt-4">
            <ToastContainer position="top-right" autoClose={5000} />
            <Navbar bg="light" expand="lg">
                <Navbar.Brand>Class Management</Navbar.Brand>
                <Navbar.Toggle aria-controls="basic-navbar-nav" />
                <Navbar.Collapse className="justify-content-end">
                    <Nav>
                        <Button variant="outline-danger" onClick={APIHandler.handleLogout}>
                            Logout
                        </Button>
                    </Nav>
                </Navbar.Collapse>
            </Navbar>

            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" />
                    <p>Loading data...</p>
                </div>
            ) : groupNotFound ? (
                // Display error message if group is not found
                <div className="text-center mt-5">
                    <h1 style={{ color: 'red', fontSize: '3rem' }}>Group Not Found</h1>
                    <p>We're sorry, but the group you're looking for does not exist.</p>
                    <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '5rem', color: 'red' }}></i> {/* Optional icon */}
                </div>
            ) : (
                <>
                    <Row className="mb-4">

                        <Col md={12}>
                            <h2>Current Students</h2>
                            <Card className="mb-4">
                                <Card.Body>
                                    <Form.Control
                                        type="number"
                                        placeholder="Enter minutes"
                                        value={globalTimeoutDuration}
                                        onChange={(e) => setGlobalTimeoutDuration(e.target.value)}
                                        style={{ width: '100px', display: 'inline-block', marginRight: '10px' }}
                                    />
                                    <Button variant="success" onClick={enableAllStudents} disabled={processingEnableDisableAll}>
                                        Enable All
                                    </Button>
                                    <Button variant="warning" onClick={disableAllStudents} disabled={processingEnableDisableAll} className="ms-3">
                                        Disable All
                                    </Button>
                                    <Table bordered hover responsive className="mt-3">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Status</th>
                                                <th>Timer</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {students.map((student, index) => (
                                                <tr key={index}>
                                                    <td>{student.name}</td>
                                                    <td>{student.email || 'No email'}</td>
                                                    <td>
                                                        <Form.Check
                                                            type="switch"
                                                            id={`student-switch-${index}`}
                                                            label={student.enabled ? 'Enabled' : 'Disabled'}
                                                            checked={student.enabled || false}
                                                            onChange={() => toggleStudentStatus(index)}
                                                        />
                                                    </td>
                                                    <td>
                                                        {student.enabled ? (
                                                            <span>{countdowns[student.email] || '00:00'}</span>
                                                        ) : (
                                                            <Form.Control
                                                                type="number"
                                                                placeholder="Minutes"
                                                                value={studentTimers[student.email] || ''}
                                                                onChange={(e) => setStudentTimers({
                                                                    ...studentTimers,
                                                                    [student.email]: e.target.value,
                                                                })}
                                                                style={{ width: '100px', display: 'inline-block' }}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Button
                                                            variant="danger"
                                                            onClick={() => deleteStudentFromFirestore(student.email)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Forms for copying users and adding students */}
                    <Row>
                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Body>
                                    <h5>Copy Users from Another Group</h5>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter source group email"
                                        value={sourceGroupEmail}
                                        onChange={(e) => setSourceGroupEmail(e.target.value)}
                                        className="mb-3"
                                    />
                                    <Button variant="primary" onClick={copyUsersFromGroup} disabled={loading}>
                                        {loading ? <Spinner animation="border" size="sm" /> : 'Copy Users'}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Body>
                                    <h5>Add New Students</h5>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter name or email"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                    />
                                    <Button variant="primary" className="mt-3" onClick={searchUsers}>
                                        Search
                                    </Button>

                                    <div className="mt-3">
                                        {searchResults.length > 0 && (
                                            <>
                                                <h5>Search Results:</h5>
                                                {searchResults.map((user, index) => (
                                                    <Badge
                                                        key={index}
                                                        pill
                                                        bg="info"
                                                        className="me-2 mb-2"
                                                        onClick={() => setSelectedUsers([...selectedUsers, user])}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {user.name} ({user.email})
                                                    </Badge>
                                                ))}
                                            </>
                                        )}
                                    </div>

                                    {selectedUsers.length > 0 && (
                                        <div className="mt-3">
                                            <h5>Selected Users:</h5>
                                            {selectedUsers.map((user, index) => (
                                                <Badge
                                                    key={index}
                                                    pill
                                                    bg="success"
                                                    className="me-2 mb-2"
                                                    onClick={() => setSelectedUsers(selectedUsers.filter(selected => selected.email !== user.email))}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {user.name} ({user.email}) <span style={{ marginLeft: '5px' }}>×</span>
                                                </Badge>
                                            ))}
                                        </div>
                                    )}

                                    <Button variant="primary" className="mt-3" onClick={addStudents}>
                                        Add Students
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {/* Loader for all API calls */}
            {processingEnableDisableAll && (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 1000,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '50%',
                    padding: '10px',
                    boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.2)'
                }}>
                    <Spinner animation="border" style={{ width: '4rem', height: '4rem' }} />
                </div>
            )}
        </Container>
    );
}

export default ClassPage;
